/**
 * AgregarAreaPresupuestoModal
 * Modal para agregar áreas al presupuesto
 * - Permite seleccionar áreas del catálogo maestro
 * - Permite crear una nueva área directamente en el maestro
 */

import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiSearch, FiCheck } from 'react-icons/fi';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { toast } from 'react-toastify';
import '../styles/modals.css';
import './AgregarAreaPresupuestoModal.css';

import Loading from './Loading';
const AgregarAreaPresupuestoModal = ({
  isOpen,
  onClose,
  presupuestoVentaId,
  presupuestoCostoId,
  tipo = 'venta',
  onSuccess
}) => {
  const [mode, setMode] = useState('select'); // 'select' o 'create'
  const [maestroAreas, setMaestroAreas] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode('select');
      setSelectedAreas([]);
      setSearchTerm('');
      setFormData({
        nombre: '',
        descripcion: ''
      });
      loadMaestroAreas();
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter areas based on search term
    if (searchTerm.trim() === '') {
      setFilteredAreas(maestroAreas);
    } else {
      const filtered = maestroAreas.filter(area =>
        area.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (area.descripcion && area.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredAreas(filtered);
    }
  }, [searchTerm, maestroAreas]);

  const loadMaestroAreas = async () => {
    setLoading(true);
    try {
      const response = await presupuestoHierarchyService.getMaestroAreas();
      const areas = response.data.results || response.data;
      setMaestroAreas(areas);
      setFilteredAreas(areas);
    } catch (error) {
      console.error('Error loading maestro areas:', error);
      toast.error('Error al cargar las áreas del catálogo');
    } finally {
      setLoading(false);
    }
  };

  const toggleAreaSelection = (areaId) => {
    if (selectedAreas.includes(areaId)) {
      setSelectedAreas(selectedAreas.filter(id => id !== areaId));
    } else {
      setSelectedAreas([...selectedAreas, areaId]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignAreas = async () => {
    if (selectedAreas.length === 0) {
      toast.warning('Selecciona al menos un área');
      return;
    }

    setCreating(true);
    try {
      // Get the complete area objects for the selected area IDs
      const selectedAreaObjects = maestroAreas.filter(area => selectedAreas.includes(area.id));

      // Assign selected areas to the presupuesto
      if (tipo === 'venta') {
        await presupuestoHierarchyService.assignCatalogAreasToVenta(presupuestoVentaId, selectedAreaObjects);
      } else {
        await presupuestoHierarchyService.assignCatalogAreasToCosto(presupuestoCostoId, selectedAreaObjects);
      }

      toast.success(`${selectedAreas.length} área(s) asignada(s) exitosamente`);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning areas:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar las áreas');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateArea = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.warning('El nombre del área es requerido');
      return;
    }

    setCreating(true);
    try {
      // Create new area in the master catalog
      const newAreaResponse = await presupuestoHierarchyService.createMaestroArea({
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim(),
        status: 'activo'
      });

      const newAreaId = newAreaResponse.data.id;

      // Assign the new area to the presupuesto
      if (tipo === 'venta') {
        await presupuestoHierarchyService.assignCatalogAreasToVenta(presupuestoVentaId, [newAreaId]);
      } else {
        await presupuestoHierarchyService.assignCatalogAreasToCosto(presupuestoCostoId, [newAreaId]);
      }

      toast.success('Área creada y asignada exitosamente');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating area:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el área');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl agregar-area-presupuesto-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Agregar Área al Presupuesto</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          <div className="mode-tabs">
            <button
              className={`mode-tab ${mode === 'select' ? 'active' : ''}`}
              onClick={() => setMode('select')}
            >
              <FiSearch /> Seleccionar del Catálogo
            </button>
            <button
              className={`mode-tab ${mode === 'create' ? 'active' : ''}`}
              onClick={() => setMode('create')}
            >
              <FiPlus /> Crear Nueva Área
            </button>
          </div>

          {mode === 'select' && (
            <div className="select-areas-section">
              <div className="info-message">
                <p>Selecciona una o más áreas del catálogo maestro para agregar a este presupuesto.</p>
              </div>

              {/* Search bar */}
              <div className="search-bar">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar áreas por nombre o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Areas list */}
              <div className="areas-list">
                {loading ? (
                  <Loading fullScreen={false} size="sm" mensaje="Cargando áreas" />
                ) : filteredAreas.length === 0 ? (
                  <div className="empty-state">
                    {searchTerm ? 'No se encontraron áreas con ese criterio' : 'No hay áreas en el catálogo maestro'}
                  </div>
                ) : (
                  filteredAreas.map(area => (
                    <div
                      key={area.id}
                      className={`area-item ${selectedAreas.includes(area.id) ? 'selected' : ''}`}
                      onClick={() => toggleAreaSelection(area.id)}
                    >
                      <div className="area-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedAreas.includes(area.id)}
                          onChange={() => {}}
                        />
                      </div>
                      <div className="area-info">
                        <h4>{area.nombre}</h4>
                        {area.descripcion && <p>{area.descripcion}</p>}
                      </div>
                      {selectedAreas.includes(area.id) && (
                        <FiCheck className="check-icon" />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Selected count */}
              {selectedAreas.length > 0 && (
                <div className="selection-summary">
                  {selectedAreas.length} área(s) seleccionada(s)
                </div>
              )}
            </div>
          )}

          {mode === 'create' && (
            <form onSubmit={handleCreateArea} className="area-form">
              <div className="form-section">
                <div className="info-message">
                  <p>Crea una nueva área en el catálogo maestro. Esta área estará disponible para todos los presupuestos y se asignará automáticamente a este presupuesto.</p>
                </div>

                <div className="form-group">
                  <label htmlFor="nombre">
                    Nombre del Área <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Ej: Obras Civiles"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="descripcion">Descripción</label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Descripción opcional del área"
                    rows="3"
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={creating}
          >
            Cancelar
          </button>
          {mode === 'select' ? (
            <button
              className="btn btn-primary"
              onClick={handleAssignAreas}
              disabled={creating || selectedAreas.length === 0}
            >
              {creating ? 'Asignando...' : (
                <>
                  <FiPlus /> Asignar Áreas ({selectedAreas.length})
                </>
              )}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleCreateArea}
              disabled={creating || !formData.nombre.trim()}
            >
              {creating ? 'Creando...' : (
                <>
                  <FiPlus /> Crear y Asignar Área
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgregarAreaPresupuestoModal;
